<script setup lang="ts">
import { ref, computed, reactive, nextTick, onMounted } from 'vue'
import DJPagination from '~/components/ui/pagination/Pagination.vue'
import Rate from '~/components/ui/rate/Rate.vue'
import localDesignerApi from '~/composables/api/localDesignerApi'
import { isEmpty } from '~/helper'
import ItemReview from './ItemReview.vue'

const props = defineProps({
  designer: {
    type: Object,
    default: null,
  },
  countryIds: {
    type: Array,
    default: () => [],
  },
})
const { $logger } = useNuxtApp()
const { isMobile } = useDevice()
const LIMIT = 5
const LIMIT_PAGINATION = isMobile ? 3 : 5

const reviews = ref<any>(null)
const sectionReviewDestinationRef = ref<HTMLElement | null>(null)
const rating = ref<number>(5)
const totalReview = ref<number>(0)
const state = reactive({
  loading: false,
  currentPage: 1,
})
const totalPage = computed(() => {
  if (isEmpty(reviews.value)) return 0
  return Math.ceil(reviews.value.total / LIMIT) || 0
})
const subTitle = computed(() => {
  if (!totalReview.value) return ''
  return totalReview.value === 1 ? `Based on ${totalReview.value} review` : `Based on ${totalReview.value} reviews`
})

async function getListReviews(page = 1) {
  try {
    state.loading = true
    if (props.designer && props.designer.id) {
      const payload = {
        limit: LIMIT,
        page,
        country_ids: props.countryIds.join(','),
      }
      const resp = (await localDesignerApi.getTravellerReviews(payload)).data.value
      if (resp?.success && resp?.data) {
        rating.value = (resp?.data as any).overall_rating
        totalReview.value = (resp?.data as any).total
        reviews.value = resp?.data as any
      } else {
        $logger.log(resp?.message, { cause: { function: 'Get list reviews destination' } })
      }
    }
  } catch (error) {
    reviews.value = { items: [], total: 0 }
    $logger.log(error, { cause: { function: 'Get list reviews destination' } })
  } finally {
    state.currentPage = page
    state.loading = false
  }
}
function fetchingData(page: number) {
  handleScroll()
  getListReviews(page)
}
function handleScroll() {
  if (sectionReviewDestinationRef.value && sectionReviewDestinationRef.value.offsetTop) {
    window.scrollTo({
      top: sectionReviewDestinationRef.value.offsetTop - 200,
      behavior: 'smooth',
    })
  }
}
onMounted(() => {
  nextTick(() => getListReviews())
})
defineExpose({
  reviews,
})
</script>

<template>
  <div v-if="reviews && reviews.total" ref="sectionReviewDestinationRef" class="relative" id="section-review-destination">
    <UiLoading class="z-4":is-active="state.loading" />
    <div class="flex items-center flex-wrap">
      <div class="flex items-center">
        <div class="mr-8 flex items-end">
          <h2 class="heading-2-bd gray-950">{{ rating }}</h2>
          <span class="heading-6-md gray-700 mb-4">/5</span>
        </div>
        <Rate class="mr-8" :rate="rating" />
      </div>
      <span class="text-lg-md mb-text-sm-rg gray-700 mb-wp-100">{{ subTitle }}</span>
    </div>
    <ItemReview v-for="review in reviews.items" :key="review.date" :review="review" />
    <DJPagination
      v-if="totalPage > 1"
      :totalRecord="reviews.total"
      :totalPage="totalPage"
      :limit="LIMIT_PAGINATION"
      v-model:currentPage="state.currentPage"
      @action="fetchingData"
    />
  </div>
</template>

<style lang="scss" scoped></style>
